import React, { useState, useEffect, useRef } from "react";
import { EyeOff, Eye, Upload, X, ChevronDown, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import TermsAndPrivacyModal, { TABS } from "./TermsAndPrivacyModal";
import toast from "react-hot-toast";
import {
  NonProfitSignUpAction,
  setPendingEmailForOTP,
  clearPendingEmailForOTP,
} from "../../Store/Actions/AuthActions";
import { SignupActionConst } from "../../Store/Constants/AuthConstants";
import {
  ALLOWED_REGISTRATION_DOCUMENT_TYPES,
  validateFileSize,
  validateFileType,
} from "../../config/fileConfig";
import { BASE_URL, API_ENDPOINTS } from "../../config/api.config";
import tarrantCountyZipCodes from "../../data/tarrant-county-zip-codes.json";

const FormSection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { loader, signupSuccess, signupError } = useSelector(
    (state) => state.auth,
  );

  const [showPassword, setShowPassword] = useState(false);
  const [confirmShowPassword, setConfirmShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName] = useState("");
  const [lastName] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [registrationDocument] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [ein, setEin] = useState("");
  const [address, setAddress] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [zipCodeSearch, setZipCodeSearch] = useState("");
  const [isZipCodeDropdownOpen, setIsZipCodeDropdownOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const zipCodeDropdownRef = useRef(null);
  const [isCheckOrganizationName, setIsCheckOrganizationName] = useState(false);
  const [isCheckEin, setIsCheckEin] = useState(false);
  const [isCheckAddress, setIsCheckAddress] = useState(false);
  const [isCheckZipCode, setIsCheckZipCode] = useState(false);
  const [isCheckPhoneNumber, setIsCheckPhoneNumber] = useState(false);
  const [isCheckEmail, setIsCheckEmail] = useState(false);
  const [isCheckPassword, setIsCheckPassword] = useState(false);
  const [isCheckConfirmPassword, setIsCheckConfirmPassword] = useState(false);
  const [isCheckServices, setIsCheckServices] = useState(false);
  const [isCheckMatch, setIsCheckMatch] = useState(false);
  const [isCheckRegistrationDocument, setIsCheckRegistrationDocument] =
    useState(false);
  const [isAgreedToTerms, setIsAgreedToTerms] = useState(false);
  const [isCheckTerms, setIsCheckTerms] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDefaultTab, setModalDefaultTab] = useState(TABS.TERMS);

  const [selectedFile, setSelectedFile] = useState(null);
  const [servicesOptions, setServicesOptions] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const servicesDropdownRef = useRef(null);

  // Email validation regex
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // EIN validation - numeric only
  const validateEin = (ein) => {
    const numericRegex = /^\d+$/;
    return numericRegex.test(ein);
  };

  // Phone number validation - + at start (optional) followed by digits only
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^(\+\d+|\d+)$/;
    return phoneRegex.test(phone);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const typeValidation = validateFileType(
      file,
      ALLOWED_REGISTRATION_DOCUMENT_TYPES,
    );
    if (!typeValidation.valid) {
      toast.error(typeValidation.error);
      e.target.value = ""; // Clear the input
      return;
    }

    // Validate file size
    const sizeValidation = validateFileSize(file);
    if (!sizeValidation.valid) {
      toast.error(sizeValidation.error);
      e.target.value = ""; // Clear the input
      return;
    }

    setSelectedFile(file);
    // Clear error state when file is selected
    if (isCheckRegistrationDocument) {
      setIsCheckRegistrationDocument(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    // Clear the file input
    const fileInput = document.getElementById("fileUpload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmShowPassword(!confirmShowPassword);
  };

  // Fetch services LOVs on component mount
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates if component unmounts

    const fetchServices = async () => {
      setIsLoadingServices(true);
      try {
        const response = await fetch(
          `${BASE_URL}${API_ENDPOINTS.GET_LOVS_PUBLIC}?key=NONPROFIT_SERVICES`,
        );
        if (response.ok) {
          const result = await response.json();
          // Backend wraps response in { statusCode, data, message, timestamp }
          const services = result?.data || result;
          if (isMounted) {
            setServicesOptions(Array.isArray(services) ? services : []);
          }
        } else {
          console.error("Failed to fetch services");
          if (isMounted) {
            toast.error("Failed to load services. Please refresh the page.");
          }
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        if (isMounted) {
          toast.error("Failed to load services. Please refresh the page.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingServices(false);
        }
      }
    };

    fetchServices();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        servicesDropdownRef.current &&
        !servicesDropdownRef.current.contains(event.target)
      ) {
        setIsServicesDropdownOpen(false);
      }
      if (
        zipCodeDropdownRef.current &&
        !zipCodeDropdownRef.current.contains(event.target)
      ) {
        setIsZipCodeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Clear all OTP-related and signup-related states when component mounts
  // This ensures clean state when navigating to signup page (handles browser back button and re-navigation)
  useEffect(() => {
    // Clear all OTP states (otpSuccess, otpError, otpLoading, pendingEmailForOTP)
    dispatch(clearPendingEmailForOTP());
    // Clear signupSuccess and signupError to prevent stale state from triggering navigation on mount
    dispatch({
      type: SignupActionConst.USER_SIGNUP_FAIL,
      result: null,
    });
  }, [dispatch]);

  // Handle signup success
  useEffect(() => {
    if (
      signupSuccess &&
      (signupSuccess.statusCode === 201 || signupSuccess.statusCode === 200)
    ) {
      // Clear signupSuccess after using it to prevent it from triggering again on remount
      dispatch({
        type: SignupActionConst.USER_SIGNUP_FAIL,
        result: null,
      });

      if (signupSuccess.data?.emailVerificationRequired === false) {
        toast.success("Account created! Your application is pending admin approval.");
        navigate("/");
      } else {
        dispatch(setPendingEmailForOTP(email));
        toast.success("Account created successfully! Please verify your email.");
        navigate("/otp");
      }
    }
  }, [signupSuccess, navigate, dispatch, email]);

  // Handle signup error
  useEffect(() => {
    if (signupError) {
      // Check if error has validation messages array
      if (signupError.message && Array.isArray(signupError.message)) {
        // Display all validation errors
        signupError.message.forEach((msg) => {
          toast.error(msg);
        });
      } else {
        const errorMessage =
          signupError.message ||
          signupError.error ||
          "Signup failed. Please try again.";
        toast.error(errorMessage);
      }
    }
  }, [signupError]);

  const signupHandler = async (e) => {
    e.preventDefault();

    // Reset error states
    setIsCheckOrganizationName(false);
    setIsCheckEin(false);
    setIsCheckAddress(false);
    setIsCheckZipCode(false);
    setIsCheckPhoneNumber(false);
    setIsCheckEmail(false);
    setIsCheckPassword(false);
    setIsCheckConfirmPassword(false);
    setIsCheckMatch(false);
    setIsCheckRegistrationDocument(false);
    setIsCheckTerms(false);
    setIsCheckServices(false);

    // Track errors to show all at once
    let hasError = false;
    let errorCount = 0;
    let hasEmailError = false;
    let hasPasswordMatchError = false;

    // Validate organization name (required)
    if (!organizationName || organizationName.trim() === "") {
      setIsCheckOrganizationName(true);
      errorCount++;
      hasError = true;
    }

    // Validate EIN (required + numeric only)
    if (!ein || ein.trim() === "") {
      setIsCheckEin(true);
      errorCount++;
      hasError = true;
    } else if (!validateEin(ein.trim())) {
      setIsCheckEin(true);
      errorCount++;
      hasError = true;
    }

    // Validate address (required)
    if (!address || address.trim() === "") {
      setIsCheckAddress(true);
      errorCount++;
      hasError = true;
    }

    // Validate zip code (required + must be from list)
    if (!zipCode || zipCode.trim() === "") {
      setIsCheckZipCode(true);
      errorCount++;
      hasError = true;
    } else if (!tarrantCountyZipCodes.includes(zipCode.trim())) {
      setIsCheckZipCode(true);
      errorCount++;
      hasError = true;
    }

    // Validate phone number (required + regex)
    if (!phoneNumber || phoneNumber.trim() === "") {
      setIsCheckPhoneNumber(true);
      errorCount++;
      hasError = true;
    } else if (!validatePhoneNumber(phoneNumber.trim())) {
      setIsCheckPhoneNumber(true);
      errorCount++;
      hasError = true;
    }

    // Validate email (required + regex)
    if (!email || email.trim() === "") {
      setIsCheckEmail(true);
      errorCount++;
      hasEmailError = true;
      hasError = true;
    } else if (!validateEmail(email)) {
      setIsCheckEmail(true);
      errorCount++;
      hasEmailError = true;
      hasError = true;
    }

    // Validate password (required + minimum 8 characters)
    let isPasswordValid = false;
    if (!password || password.trim() === "") {
      setIsCheckPassword(true);
      errorCount++;
      hasError = true;
    } else if (password.trim().length < 8) {
      setIsCheckPassword(true);
      errorCount++;
      hasError = true;
    } else {
      // Password is valid (not empty and at least 8 characters)
      isPasswordValid = true;
    }

    // Validate confirm password (required)
    if (!confirmPassword || confirmPassword.trim() === "") {
      setIsCheckConfirmPassword(true);
      errorCount++;
      hasError = true;
    } else if (
      isPasswordValid &&
      confirmPassword.trim() !== "" &&
      password !== confirmPassword
    ) {
      // Validate password match only if password is valid (8+ chars) and both passwords are entered
      setIsCheckConfirmPassword(true);
      setIsCheckMatch(true);
      errorCount++;
      hasPasswordMatchError = true;
      hasError = true;
    }

    if (!selectedServices || selectedServices.length === 0) {
      setIsCheckServices(true);
      errorCount++;
      hasError = true;
    }

    // Validate registration document (required)
    if (!selectedFile) {
      setIsCheckRegistrationDocument(true);
      errorCount++;
      hasError = true;
    }

    // Validate terms and conditions checkbox (required)
    if (!isAgreedToTerms) {
      setIsCheckTerms(true);
      errorCount++;
      hasError = true;
    }

    // If there are errors, show toast and return
    if (hasError) {
      // Prioritize password match error only if passwords don't match (even if other errors exist)
      if (hasPasswordMatchError) {
        toast("Passwords do not match", { icon: "⚠️" });
      } else if (errorCount > 1) {
        toast("Please fill out all required fields", { icon: "⚠️" });
      } else if (hasEmailError) {
        toast("Please enter a valid email", { icon: "⚠️" });
      } else if (!organizationName || organizationName.trim() === "") {
        toast("Please enter your organization name", { icon: "⚠️" });
      } else if (!ein || ein.trim() === "" || !validateEin(ein.trim())) {
        toast("Please enter a valid EIN (numbers only)", { icon: "⚠️" });
      } else if (!selectedServices || selectedServices.length === 0) {
        toast("Please select at least one service", { icon: "⚠️" });
      } else if (!address || address.trim() === "") {
        toast("Please enter your address", { icon: "⚠️" });
      } else if (!zipCode || zipCode.trim() === "" || !tarrantCountyZipCodes.includes(zipCode.trim())) {
        toast("Please select a valid ZIP code", { icon: "⚠️" });
      } else if (
        !phoneNumber ||
        phoneNumber.trim() === "" ||
        !validatePhoneNumber(phoneNumber.trim())
      ) {
        toast("Please enter a valid phone number", { icon: "⚠️" });
      } else if (
        !password ||
        password.trim() === "" ||
        password.trim().length < 8
      ) {
        toast("Password must be at least 8 characters", { icon: "⚠️" });
      } else if (!confirmPassword || confirmPassword.trim() === "") {
        toast("Please confirm your password", { icon: "⚠️" });
      } else if (!selectedFile) {
        toast("Please upload registration document", { icon: "⚠️" });
      } else if (!isAgreedToTerms) {
        toast("Please agree to the terms and conditions", { icon: "⚠️" });
      }
      return;
    }

    // Validate file if selected
    if (selectedFile) {
      const typeValidation = validateFileType(
        selectedFile,
        ALLOWED_REGISTRATION_DOCUMENT_TYPES,
      );
      if (!typeValidation.valid) {
        toast.error(typeValidation.error);
        return;
      }

      const sizeValidation = validateFileSize(selectedFile);
      if (!sizeValidation.valid) {
        toast.error(sizeValidation.error);
        return;
      }
    }

    // Dispatch nonprofit signup action
    dispatch(
      NonProfitSignUpAction({
        email,
        password,
        firstName,
        lastName,
        organizationName,
        ein,
        address,
        zipCode,
        phoneNumber,
        registrationDocuments: registrationDocument,
        file: selectedFile,
        serviceIds: selectedServices,
      }),
    );
  };

  return (
    <>
    <TermsAndPrivacyModal
      isOpen={isModalOpen}
      defaultTab={modalDefaultTab}
      onClose={() => setIsModalOpen(false)}
    />
    <form className="w-full mt-3" onSubmit={signupHandler}>
      <div class="relative w-full">
        <input
          type="text"
          placeholder="Organization Name"
          value={organizationName}
          onChange={(e) => {
            setOrganizationName(e.target.value);
            if (isCheckOrganizationName) setIsCheckOrganizationName(false);
          }}
          class={`w-full font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out ${
            isCheckOrganizationName
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
              : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          }`}
        />
      </div>
      <div className="flex flex-col lg:flex-row lg:justify-between mt-3">
        <div className="relative w-full lg:w-[49%]">
          <input
            type="text"
            placeholder="EIN (Tax ID)"
            value={ein}
            onChange={(e) => {
              // Only allow numeric input
              const value = e.target.value.replace(/\D/g, "");
              setEin(value);
              if (isCheckEin) setIsCheckEin(false);
            }}
            class={`w-full font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out ${
              isCheckEin
                ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
            }`}
          />
        </div>
        <div
          className="relative w-full lg:w-[49%] mt-3 lg:mt-0"
          ref={servicesDropdownRef}
        >
          <button
            type="button"
            onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
            className={`w-full font-popinsMedium rounded-md border-2 bg-white pl-3 pr-10 py-1 outline-none transition-all duration-300 ease-in-out appearance-none cursor-pointer text-left h-[42px] flex items-center relative
  ${
    isCheckServices
      ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
      : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
  }
  ${selectedServices.length === 0 ? "text-gray-400" : "text-black/50"}
`}
            disabled={isLoadingServices}
          >
            <div
              className={`flex-1 flex flex-nowrap gap-1.5 items-center min-w-0 pr-3 pb-1 ${
                selectedServices.length > 0
                  ? "overflow-x-auto [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
                  : "overflow-hidden"
              }`}
            >
              {isLoadingServices ? (
                <span className="text-gray-400 whitespace-nowrap">
                  Loading services...
                </span>
              ) : selectedServices.length === 0 ? (
                <span className="text-gray-400 whitespace-nowrap">
                  Services Offered
                </span>
              ) : (
                selectedServices.map((serviceId) => {
                  const service = servicesOptions.find(
                    (s) => s.id === serviceId,
                  );
                  if (!service) return null;
                  return (
                    <span
                      key={serviceId}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#3FC4E3]/10 text-[#3FC4E3] rounded-md text-xs font-popinsMedium whitespace-nowrap flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedServices(
                          selectedServices.filter((id) => id !== serviceId),
                        );
                      }}
                    >
                      <span>{service.name}</span>
                      <X
                        size={12}
                        className="cursor-pointer hover:text-[#006077] transition-colors flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedServices(
                            selectedServices.filter((id) => id !== serviceId),
                          );
                        }}
                      />
                    </span>
                  );
                })
              )}
            </div>
            <ChevronDown
              size={20}
              className={`text-black/50 transition-transform absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${
                isServicesDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {isServicesDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {servicesOptions.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No services available
                </div>
              ) : (
                servicesOptions.map((service) => {
                  const isSelected = selectedServices.includes(service.id);
                  return (
                    <div
                      key={service.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedServices(
                            selectedServices.filter((id) => id !== service.id),
                          );
                        } else {
                          setSelectedServices([
                            ...selectedServices,
                            service.id,
                          ]);
                        }

                        if (isCheckServices) setIsCheckServices(false);
                      }}
                      className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                    >
                      <div
                        className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                          isSelected
                            ? "border-[#3FC4E3] bg-[#3FC4E3]"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected && (
                          <Check size={14} className="text-white" />
                        )}
                      </div>
                      <span className="font-popinsRegular text-sm text-black/50">
                        {service.name}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:justify-between mt-3">
        <div className="relative w-full lg:w-[49%]">
          <input
            type="text"
            placeholder="Address (Street, City, State)"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              if (isCheckAddress) setIsCheckAddress(false);
            }}
            className={`w-full font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out ${
              isCheckAddress
                ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
            }`}
          />
        </div>
        <div className="relative w-full lg:w-[49%] mt-3 lg:mt-0" ref={zipCodeDropdownRef}>
          <input
            type="text"
            placeholder="ZIP Code"
            value={zipCodeSearch}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              setZipCodeSearch(val);
              setZipCode("");
              setIsZipCodeDropdownOpen(true);
              if (isCheckZipCode) setIsCheckZipCode(false);
            }}
            onFocus={() => setIsZipCodeDropdownOpen(true)}
            onBlur={() => {
              // Delay to allow click on dropdown item to register
              setTimeout(() => {
                if (!zipCode) {
                  setZipCodeSearch("");
                }
              }, 200);
            }}
            className={`w-full font-popinsMedium rounded-md border-2 bg-white px-4 pr-10 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out ${
              isCheckZipCode
                ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
            }`}
          />
          <ChevronDown
            size={20}
            className={`text-black/50 transition-transform absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${
              isZipCodeDropdownOpen ? "rotate-180" : ""
            }`}
          />
          {isZipCodeDropdownOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border-2 border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {(() => {
                const filtered = tarrantCountyZipCodes.filter((z) =>
                  z.startsWith(zipCodeSearch)
                );
                if (filtered.length === 0) {
                  return (
                    <div className="px-4 py-2 text-sm text-black/50 font-popinsMedium">
                      No Match Found
                    </div>
                  );
                }
                return filtered.map((z) => (
                  <div
                    key={z}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setZipCode(z);
                      setZipCodeSearch(z);
                      setIsZipCodeDropdownOpen(false);
                      if (isCheckZipCode) setIsCheckZipCode(false);
                    }}
                    className="px-4 py-2 text-sm font-popinsMedium text-black/50 cursor-pointer hover:bg-[#3FC4E3]/10"
                  >
                    {z}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>
      <div class="relative w-full mt-3">
        <input
          type="text"
          placeholder="Phone Number"
          value={phoneNumber}
          onChange={(e) => {
            let value = e.target.value;
            // Allow + only at the start, then only digits
            if (value.startsWith("+")) {
              // If starts with +, allow + followed by digits only
              value = "+" + value.slice(1).replace(/\D/g, "");
            } else {
              // If doesn't start with +, allow only digits
              value = value.replace(/\D/g, "");
            }
            setPhoneNumber(value);
            if (isCheckPhoneNumber) setIsCheckPhoneNumber(false);
          }}
          class={`w-full font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out ${
            isCheckPhoneNumber
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
              : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          }`}
        />
      </div>
      <div class="relative w-full mt-3">
        <input
          type="text"
          placeholder="Email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (isCheckEmail) setIsCheckEmail(false);
          }}
          class={`peer w-full font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 outline-none transition-all duration-300 ease-in-out ${
            isCheckEmail
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
              : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          }`}
        />
      </div>
      <div className="relative w-full mt-3">
        {selectedFile ? (
          <div
            className={`flex items-center justify-between p-2 bg-gray-50 rounded border ${
              isCheckRegistrationDocument ? "border-red-500" : ""
            }`}
          >
            <span className="font-popinsRegular text-sm text-gray-900 truncate flex-1">
              {selectedFile.name}
            </span>
            <button
              type="button"
              onClick={handleRemoveFile}
              className="ml-3 text-red-500 hover:text-red-700 transition-colors"
              title="Remove file"
            >
              <X size={18} />
            </button>
          </div>
        ) : (
          <label
            htmlFor="fileUpload"
            className={`flex items-center justify-between w-full cursor-pointer font-popinsMedium rounded-md border-2 bg-white px-4 py-2 text-black/50 transition-all duration-300 ease-in-out ${
              isCheckRegistrationDocument
                ? "border-red-500 hover:border-red-500 hover:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
                : "border-border hover:border-[#3FC4E3] hover:shadow-[0_0_3px_#3FC4E3]"
            }`}
          >
            <span className="truncate">IRS Determination Letter</span>
            <Upload size={18} className="text-black/50 ml-2" />
          </label>
        )}
        <input
          id="fileUpload"
          type="file"
          onChange={handleFileChange}
          className="hidden"
          accept=".pdf,.doc,.docx"
        />
      </div>
      <div class="relative w-full mt-3">
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (isCheckPassword) setIsCheckPassword(false);
            if (isCheckMatch) {
              setIsCheckMatch(false);
              setIsCheckConfirmPassword(false);
            }
          }}
          class={`w-full font-popinsMedium rounded-xl border-2 bg-white px-4 py-2 !text-black/50 outline-none transition-all duration-300 ease-in-out ${
            isCheckPassword || isCheckMatch
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
              : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          }`}
        />
        <div
          title="Show password"
          className="absolute right-3 top-3 cursor-pointer text-black/50"
          onClick={togglePasswordVisibility}
        >
          {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
        </div>
      </div>
      <div class="relative w-full mt-3">
        <input
          type={confirmShowPassword ? "text" : "password"}
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (isCheckConfirmPassword || isCheckMatch) {
              setIsCheckConfirmPassword(false);
              setIsCheckMatch(false);
            }
          }}
          class={`w-full font-popinsMedium rounded-xl border-2 bg-white px-4 py-2 !text-black/50 outline-none transition-all duration-300 ease-in-out ${
            isCheckConfirmPassword || isCheckMatch
              ? "border-red-500 focus:border-red-500 focus:shadow-[0_0_3px_rgba(239,68,68,0.5)]"
              : "border-border focus:border-[#3FC4E3] focus:shadow-[0_0_3px_#3FC4E3]"
          }`}
        />
        <div
          title="Show password"
          className="absolute right-3 top-3 cursor-pointer text-black/50"
          onClick={toggleConfirmPasswordVisibility}
        >
          {confirmShowPassword ? <Eye size={20} /> : <EyeOff size={20} />}
        </div>
      </div>
      <div className="flex justify-center mt-2">
        <div
          className={`w-[70%] flex items-start gap-2 ${
            isCheckTerms ? "text-red-500" : ""
          }`}
        >
          <input
            type="checkbox"
            id="termsCheckbox"
            checked={isAgreedToTerms}
            onChange={(e) => {
              setIsAgreedToTerms(e.target.checked);
              if (isCheckTerms) setIsCheckTerms(false);
            }}
            className={`mt-1 w-4 h-4 rounded border-2 cursor-pointer transition-all duration-300 ease-in-out ${
              isCheckTerms
                ? "border-red-500 accent-red-500"
                : "border-border accent-themeColor"
            } focus:ring-2 focus:ring-themeColor focus:ring-offset-0`}
          />
          <label
            htmlFor="termsCheckbox"
            className="text-center cursor-pointer flex-1"
          >
            <p className="text-sm sm:text-base">
              I hereby agree to all the contents of the{" "}
              <button
                type="button"
                onClick={() => { setModalDefaultTab(TABS.TERMS); setIsModalOpen(true); }}
                className="text-themeColor hover:underline font-popinsMedium"
              >
                Terms of Use
              </button>{" "}
              and{" "}
              <button
                type="button"
                onClick={() => { setModalDefaultTab(TABS.PRIVACY); setIsModalOpen(true); }}
                className="text-themeColor hover:underline font-popinsMedium"
              >
                Privacy Policy
              </button>
            </p>
          </label>
        </div>
      </div>
      <button
        type="submit"
        disabled={loader}
        className="relative overflow-hidden py-3 bg-themeColor text-white font-popinsSemiBold text-base mt-5 rounded-md shadow-lg
                 transform transition duration-300 ease-in-out
                 hover:bg-[#3FC4E3] hover:scale-105 hover:shadow-[0_0_20px_rgba(63,196,227,0.6)] 
                 active:scale-95 active:shadow-inner focus:outline-none w-full
                 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
      >
        <span className="flex items-center justify-center">
          {loader ? (
            <>
              <svg
                aria-hidden="true"
                class="w-6 h-6 text-gray-200 animate-spin dark:text-white fill-indigo-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
              <span class="ml-2">Submitting...</span>
            </>
          ) : (
            "Submit for Approval"
          )}
        </span>
        <span className="absolute inset-0 rounded-full bg-white opacity-0 active:opacity-10 transition-opacity duration-300"></span>
      </button>
    </form>
    </>
  );
};

export default FormSection;
